import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PeptideCard } from '../components/PeptideCard';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CatalogPage() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [peptides, setPeptides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/peptides/categories?lang=${language}`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [language]);

  useEffect(() => {
    const fetchPeptides = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('lang', language);
        if (selectedCategory && selectedCategory !== 'all') {
          params.append('category', selectedCategory);
        }
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        const response = await axios.get(`${API}/peptides?${params.toString()}`);
        setPeptides(response.data);
      } catch (error) {
        console.error('Error fetching peptides:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPeptides();
  }, [selectedCategory, searchTerm, language]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSearchParams({});
  };

  const hasFilters = searchTerm || (selectedCategory && selectedCategory !== 'all');

  return (
    <div className="min-h-screen bg-slate-50 pt-20 lg:pt-24" data-testid="catalog-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-custom py-8">
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="catalog-title"
          >
            {t('nav.catalog')}
          </h1>
          <p className="text-slate-600">
            {peptides.length} {peptides.length === 1 ? t('products.productAvailable') : t('products.productsAvailable')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 sticky top-16 lg:top-20 z-30">
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder={t('products.searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 bg-white border-slate-200 rounded-sm"
                data-testid="search-input"
              />
            </div>

            {/* Category Filter */}
            <div className="w-full sm:w-64">
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="bg-white border-slate-200 rounded-sm" data-testid="category-select">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder={t('products.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.allCategories')}</SelectItem>
                  {categories.map((category) => {
                    // Handle both translated (object) and simple (string) formats
                    const categoryValue = typeof category === 'object' ? category.en : category;
                    const categoryLabel = typeof category === 'object' ? category.translated : category;
                    return (
                      <SelectItem key={categoryValue} value={categoryValue}>
                        {categoryLabel}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm transition-colors"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4" />
                {t('products.clear')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container-custom py-8">
        {loading ? (
          <div className="grid-products">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 p-6 rounded-sm animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/4 mb-3" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-4" />
                <div className="h-16 bg-slate-200 rounded mb-4" />
                <div className="h-8 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        ) : peptides.length === 0 ? (
          <div className="text-center py-16" data-testid="no-results">
            <p className="text-slate-500 text-lg">{t('products.noResults')}</p>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 btn-secondary"
                data-testid="clear-filters-empty"
              >
                {t('products.clearFilters')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid-products">
            {peptides.map((peptide, index) => (
              <div
                key={peptide.cat_no}
                className="animate-fadeIn"
                style={{ animationDelay: `${index * 0.03}s` }}
              >
                <PeptideCard peptide={peptide} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
